import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { ItemListRequest, ItemListResponse, ItemResponse, SaveResponse } from 'helpers/interfaces';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { WhosNext, IssueServiceType } from 'helpers/enums';
import { KeyValuePair } from 'helpers';

interface IContext {
    isLoading: boolean
    ticket: ITicket
    ticketsWithSameSerialNumber: ITicketWithSerialNumber[]
    assignableUsers: IUser[]
    watcherUsers: IUser[]
    activities: IActivities[]
    recordCount: number
    search: ISearchActivities
    statusDialog: IStatusDialogState
    productSuppliers: KeyValuePair<string, string>[]
    suggestions: IAssegneeSuggestions[]

    setStatusDialog: (v: IStatusDialogState) => void
    getTicket: () => Promise<void>
    changePage: (n: number) => void
    updateTicketStatus: (statusId: string) => Promise<void>
    updateWarrantyRegistration: (registrationId: string) => void
    updateCustomerServiceAssignee: (csAssignee: string) => Promise<void>
    updateAssignee: (assignee: string) => Promise<void>
    updateTicketPriority: (priorityId: string) => Promise<void>
    updateWatchers: (watchers: string[]) => Promise<void>
    updateGroups: (groups: string[]) => Promise<void>
    updateWhosNext: (whosNext: WhosNext) => Promise<void>
    updateProductSuppliers: (suppliers: KeyValuePair<string, string>[]) => void
}

interface IUpdateTicketStatusResponse {
    changed: string
    error: string
}

export interface IStatusDialogState {
    open: boolean
    suggestedStatus?: string
    error?: string
}

export interface IActivities {
    message: string
    author: string
    date: string
}

interface ITicketResponse {
    assignableUsers: IUser[]
    watcherUsers: IUser[]
    productSuppliers: KeyValuePair<string, string>[]
    assegneeSuggestions: IAssegneeSuggestions[]
    ticket: ITicket
    ticketsWithSameSerialNumber: ITicketWithSerialNumber[]
}

interface ISearchActivities extends ItemListRequest {
    ticketId: string
}

interface ITicketWithSerialNumber {
    id: string
    ticketNumber: string
    title: string
    createdOn: moment.Moment
    ticketStatusId: string
    ticketPriorityId: string
    ticketTypeId: string
    assignee: string
    customerServiceAssignee: string
}

interface IUser {
    email: string
    name: string
    groups: string[]
}

type ITicket = {
    id: string
    isReadOnly: boolean
    informationsIsMissing: boolean
    serialNumberIsMissing: boolean
    hasCustomerServiceAssignee: boolean
    ticketNumber: string
    title: string
    ticketTypeId: string
    ticketStatusId: string
    whosNext: WhosNext
    ticketPriorityId: string
    reporterM3id: string
    hasSerialNumber: boolean
    supplier: string
    groups: string[]
    watchers: string[]
    reporter: string
    reporterName: string
    reporterCompany: string
    assignee: string
    customerServiceAssignee: string
    createdBy: string
    lastUpdatedBy: string
    createdOn: moment.Moment
    lastUpdatedOn: moment.Moment

    deliveryNote: string
    invoiceDate: moment.Moment
    invoiceNumber: string
    invoiceQuantity?: number
    receivedQuantity?: number
    damagedItemQuantity?: number
    packageNumbers: string[]
    numberOfPackages?: number
    issueServiceType: IssueServiceType
    isManual: boolean
    canSeeOnPIM: boolean
    productSKU: string
    productBrand: string
    productDescription: string
    productQuantity?: string
    productSupplierCode: string
    productSupplierName: string
    hasPurchaseReceipt?: boolean
    productIsNew?: boolean
    productIsUnderWarranty?: boolean
    canYouRepairProductYourself?: boolean
    serialNumber: string
    suggestedAction: string
    purchaseDate: moment.Moment
    purchaseCountry: string

    // warranty
    endCustomersHasRegisteredProduct?: boolean
    endCustomerEmailAddress?: string
    productQRCode?: string
    registrationId?: string

    // date
    customerServiceAssignedOn: moment.Moment
    resolvedOn: moment.Moment
    cancelledOn: moment.Moment
    closedOn: moment.Moment
    completedOn: moment.Moment
}

interface IAssegneeSuggestions {
    id: string
    productStatus: string
    product: string
    assignee: string
}

export let ticketContext = React.createContext({} as IContext);

let { Provider } = ticketContext;

let TicketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { id } = useParams<{ id: string }>();
    let { enqueueSnackbar } = useSnackbar();

    const [statusDialog, setStatusDialog] = useState<IStatusDialogState>({
        error: "",
        open: false
    });

    let [isLoading, setLoading] = useState(true);
    let [assignableUsers, setAssignableUsers] = useState<IUser[]>([]);
    let [watcherUsers, setWatcherUsers] = useState<IUser[]>([]);
    let [ticket, setTicket] = useState({} as ITicket);
    let [productSuppliers, setProductSuppliers] = useState<KeyValuePair<string, string>[]>([]);
    let [ticketsWithSameSerialNumber, setTicketsWithSameSerialNumber] = useState<ITicketWithSerialNumber[]>([]);

    // activities
    let [activities, setActivities] = useState<IActivities[]>([]);
    let [recordCount, setRecordCount] = useState<number>(0);
    let [search, setSearch] = useState<ISearchActivities>({
        pageNr: 0,
        pageSize: 10,
        ticketId: ''
    });

    let [suggestions, setSuggestions] = useState<IAssegneeSuggestions[]>([]);

    useEffect(() => {
        getTicket();
    }, [id]);

    let getTicket = async () => {
        try {
            let res = await axios.get<ItemResponse<ITicketResponse>>('/api/Ticket/GetTicket', { params: { ticketNumber: id } });
            let { item } = res.data;

            setAssignableUsers(item.assignableUsers);
            setWatcherUsers(item.watcherUsers);
            setProductSuppliers(item.productSuppliers);
            setTicketsWithSameSerialNumber(item.ticketsWithSameSerialNumber);

            setSuggestions(item.assegneeSuggestions);
            setTicket({
                ...item.ticket,
                assignee: item.ticket.assignee || '',
                customerServiceAssignee: item.ticket.customerServiceAssignee || '',
                groups: item.ticket.groups || [],
                watchers: item.ticket.watchers || [],
                hasSerialNumber: item.ticket.hasSerialNumber || false
            });

            getTicketActivities({ ...search, pageNr: 0, ticketId: item.ticket.id });

        } catch (err) { }
        finally {
            setLoading(false);
        }
    }
    let updateWarrantyRegistration = (registrationId: string) => {
        setTicket({ ...ticket, registrationId: registrationId })
    }

    let getTicketActivities = async (searchRequest: ISearchActivities) => {
        try {
            setSearch(searchRequest);

            let response = await axios.get<ItemListResponse<IActivities>>('/api/Ticket/GetTicketActivities', {
                params: searchRequest
            });
            setActivities(response.data.items);
            setRecordCount(response.data.recordCount);

        } catch (error) { }
    }

    let updateTicketStatus = async (ticketStatusId: string) => {
        let oldStatus = ticket.ticketStatusId;
        if (oldStatus !== ticketStatusId) {
            try {
                var response = await axios.post<IUpdateTicketStatusResponse>('/api/Ticket/UpdateTicketStatus', { value: ticketStatusId, ticketId: ticket.id });

                if (response.data.changed) {
                    setTicket({ ...ticket, ticketStatusId });
                    getTicketActivities({ ...search, pageNr: 0, ticketId: ticket.id });
                    enqueueSnackbar("Saved changes", { variant: "success" });
                    setStatusDialog({
                        open: false
                    })
                } else {
                    setStatusDialog({
                        error: response.data.error,
                        open: true
                    })
                }


            } catch (err) {
                setTicket({ ...ticket, ticketStatusId: oldStatus });
            }
        } else {
            setStatusDialog({
                open: false
            })
        }


    }

    let updateTicketPriority = async (ticketPriorityId: string) => {
        let oldPriority = ticket.ticketPriorityId;

        try {
            await axios.post<SaveResponse>('/api/Ticket/UpdateTicketPriority', { value: ticketPriorityId, ticketId: ticket.id });

            setTicket({ ...ticket, ticketPriorityId });
            getTicketActivities({ ...search, pageNr: 0, ticketId: ticket.id });

            enqueueSnackbar("Saved changes", { variant: "success" });

        } catch (err) {
            setTicket({ ...ticket, ticketPriorityId: oldPriority });
        }
    }

    let updateCustomerServiceAssignee = async (customerServiceAssignee: string) => {
        let oldCsAssignee = ticket.customerServiceAssignee;

        try {
            let res = await axios.post<SaveResponse>('/api/Ticket/UpdateCustomerServiceAssignee', { value: customerServiceAssignee, ticketId: ticket.id });

            setTicket({ ...ticket, customerServiceAssignee });
            getTicket();

            enqueueSnackbar("Saved changes", { variant: "success" });
            setStatusDialog({ open: true, suggestedStatus: res.data.ticketStatusId });

        } catch (err) {
            setTicket({ ...ticket, customerServiceAssignee: oldCsAssignee });
        }
    }

    let updateAssignee = async (assignee: string) => {
        let oldAssignee = ticket.assignee;

        try {
            let res = await axios.post<SaveResponse>('/api/Ticket/UpdateAssignee', { value: assignee, ticketId: ticket.id });

            setTicket({ ...ticket, assignee });
            getTicket();

            enqueueSnackbar("Saved changes", { variant: "success" });
            setStatusDialog({ open: true, suggestedStatus: res.data.ticketStatusId })

        } catch (err) {
            setTicket({ ...ticket, assignee: oldAssignee });
        }
    }

    let updateWatchers = async (watchers: string[]) => {
        let oldWatchers = ticket.watchers || [];

        try {

            await axios.post<SaveResponse>('/api/Ticket/UpdateWatchers', { value: watchers, ticketId: ticket.id });

            setTicket({ ...ticket, watchers });
            getTicket();

            enqueueSnackbar("Saved changes", { variant: "success" });

        } catch (err) {
            setTicket({ ...ticket, watchers: oldWatchers });
        }
    }

    let updateGroups = async (groups: string[]) => {
        let oldGroups = ticket.groups || [];

        try {
            await axios.post<SaveResponse>('/api/Ticket/UpdateGroups', { value: groups, ticketId: ticket.id });

            setTicket({ ...ticket, groups });
            getTicket();
            enqueueSnackbar("Saved changes", { variant: "success" });

        } catch (err) {
            setTicket({ ...ticket, groups: oldGroups });
        }
    }

    let updateWhosNext = async (whosNext: WhosNext) => {
        let oldWhosNext = ticket.whosNext;

        try {
            await axios.post<SaveResponse>('/api/Ticket/UpdateWhosNext', { value: whosNext, ticketId: ticket.id });

            setTicket({ ...ticket, whosNext });
            getTicketActivities({ ...search, pageNr: 0, ticketId: ticket.id });

            enqueueSnackbar("Saved changes", { variant: "success" });

        } catch (err) {
            setTicket({ ...ticket, whosNext: oldWhosNext });
        }
    }

    let changePage = (page: number) => {
        let request = {
            ...search,
            pageNr: page,
            ticketId: ticket.id
        }

        setSearch(request)
        getTicketActivities(request);

        window.scrollTo(0, 0);
    }

    let updateProductSuppliers = (suppliers: KeyValuePair<string, string>[]) => {
        setProductSuppliers(suppliers);
    }


    return (
        <Provider
            value={{
                isLoading,
                ticket,
                assignableUsers,
                watcherUsers,
                activities,
                recordCount,
                search,
                statusDialog,
                productSuppliers,
                ticketsWithSameSerialNumber,
                suggestions,

                setStatusDialog,
                updateWarrantyRegistration,
                getTicket,
                changePage,
                updateAssignee,
                updateCustomerServiceAssignee,
                updateGroups,
                updateTicketPriority,
                updateTicketStatus,
                updateWatchers,
                updateWhosNext,
                updateProductSuppliers
            }}
        >
            {children}
        </Provider>
    )
}

export default TicketProvider;